<template>
  <div class="demo">
    <div class="tabs" role="tablist" aria-label="Les étapes d'un dossier de facturation" @keydown="onKeydown">
      <button
          v-for="(step, index) in steps"
          :key="step.tab"
          :id="'demo-tab-' + index"
          ref="tabs"
          type="button"
          role="tab"
          class="tab"
          :class="{'tab--active': index === stage, 'tab--done': index < stage}"
          :aria-selected="index === stage ? 'true' : 'false'"
          aria-controls="demo-panel"
          :tabindex="index === stage ? 0 : -1"
          @click="select(index)">
        {{ step.tab }}
      </button>
    </div>

    <div class="sheet-wrap">
      <div id="demo-panel" class="sheet" role="tabpanel" tabindex="0" :aria-labelledby="'demo-tab-' + stage">
        <header class="head">
          <div>
            <p class="kind">{{ current.kind }}</p>
            <p class="number tabular">{{ current.number }}<span v-if="current.reference"> · {{ current.reference }}</span></p>
          </div>
          <p class="sample">Exemple fictif</p>
        </header>

        <p class="parties">
          <span><strong>Studio Lumière</strong> pour</span>
          <span><strong>Boulangerie Martin</strong></span>
        </p>

        <ul class="lines">
          <li v-for="line in current.lines" :key="line.label">
            <span class="label">{{ line.label }}<small v-if="line.detail">{{ line.detail }}</small></span>
            <span class="amount tabular">{{ euro(line.amount) }}</span>
          </li>
        </ul>

        <dl class="totals tabular">
          <div>
            <dt>Total HT</dt>
            <dd>{{ euro(current.ht) }}</dd>
          </div>
          <div>
            <dt>TVA 20 %</dt>
            <dd>{{ euro(current.vat) }}</dd>
          </div>
          <div class="total">
            <dt>Total TTC</dt>
            <dd>{{ euro(current.ttc) }}</dd>
          </div>
          <div v-for="deduction in current.deductions" :key="deduction.label" class="deduction">
            <dt>{{ deduction.label }}</dt>
            <dd>− {{ euro(deduction.amount) }}</dd>
          </div>
          <div v-if="current.due !== null" class="due">
            <dt>Reste à payer</dt>
            <dd><mark>{{ euro(current.due) }}</mark></dd>
          </div>
        </dl>

        <p v-if="current.footnote" class="footnote">{{ current.footnote }}</p>

        <span :key="stage" class="stamp" :class="'stamp--' + current.tone">{{ current.stamp }}</span>
      </div>
    </div>

    <p class="caption" aria-live="polite">
      {{ current.caption }}
      <router-link to="/gestionnaire-citron/devis-factures">Voir comment ça marche</router-link>
    </p>
  </div>
</template>

<script>
  const money = new Intl.NumberFormat('fr-FR', {style: 'currency', currency: 'EUR'});

  const LINES = [
    {label: 'Maquette et design de 5 pages', detail: '1 × 400,00 €', amount: 400},
    {label: 'Intégration et mise en ligne', detail: '1 × 350,00 €', amount: 350},
    {label: 'Référencement de base', detail: '1 × 200,00 €', amount: 200},
  ];

  const STEPS = [
    {
      tab: 'Devis',
      kind: 'Devis',
      number: 'D0028',
      lines: LINES,
      ht: 950, vat: 190, ttc: 1140,
      deductions: [],
      due: null,
      footnote: 'Valable 30 jours. Acompte demandé à la signature : 342,00 € (30 %).',
      stamp: 'Signé',
      tone: 'ok',
      caption: 'Le client scanne le QR code du devis et le signe depuis son téléphone.',
    },
    {
      tab: 'Acompte',
      kind: 'Facture d\'acompte',
      number: 'F0058',
      reference: 'devis D0028',
      lines: [{label: 'Acompte de 30 % sur le devis D0028', detail: 'Montant repris du devis', amount: 285}],
      ht: 285, vat: 57, ttc: 342,
      deductions: [],
      due: null,
      footnote: 'Facture d\'acompte payée à la signature.',
      stamp: 'Payée',
      tone: 'ok',
      caption: 'L\'acompte se facture depuis le devis : aucune ligne à ressaisir.',
    },
    {
      tab: 'Facture',
      kind: 'Facture',
      number: 'F0059',
      reference: 'devis D0028',
      lines: LINES,
      ht: 950, vat: 190, ttc: 1140,
      deductions: [{label: 'Acompte F0058 déjà payé', amount: 342}],
      due: 798,
      footnote: 'Le client ne règle que le solde.',
      stamp: 'À régler',
      tone: 'wait',
      caption: 'La facture finale reprend le devis et retire l\'acompte déjà payé. Elle peut être produite au format Factur-X.',
    },
    {
      tab: 'Encaissée',
      kind: 'Facture',
      number: 'F0059',
      reference: 'devis D0028',
      lines: LINES,
      ht: 950, vat: 190, ttc: 1140,
      deductions: [{label: 'Acompte F0058 déjà payé', amount: 342}, {label: 'Virement reçu', amount: 798}],
      due: 0,
      footnote: 'Solde réglé par virement.',
      stamp: 'Payée',
      tone: 'ok',
      caption: 'Le virement est enregistré : le solde tombe à zéro et la TVA encaissée se met à jour dans la comptabilité.',
    },
  ];

  /**
   * Démonstration du cycle d'un dossier (devis, acompte, facture, encaissement) sur un exemple fictif.
   * Les montants sont cohérents avec le guide « Facture d'acompte et facture finale » : 950 € HT, acompte de 342 € TTC, solde de 798 €.
   * Onglets accessibles au clavier (flèches, Début, Fin).
   */
  export default {
    name: 'DocumentDemo',
    data: function () {
      return {
        steps: STEPS,
        stage: 0,
      }
    },
    computed: {
      current() {
        return this.steps[this.stage];
      },
    },
    methods: {
      euro(value) {
        return money.format(value);
      },
      select(index) {
        this.stage = index;
      },
      onKeydown(event) {
        const last = this.steps.length - 1;
        let next = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = this.stage === last ? 0 : this.stage + 1;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = this.stage === 0 ? last : this.stage - 1;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = last;
        if (next === null) return;
        event.preventDefault();
        this.stage = next;
        this.$nextTick(() => {
          const tabs = this.$refs.tabs;
          if (tabs && tabs[next]) tabs[next].focus();
        });
      },
    },
  }
</script>

<style lang="stylus" scoped>
@import '~/src/common/style/constants.styl';

.demo
  width 100%
  max-width 520px
  margin 0 auto

// ---------------------------------------------------------- onglets
.tabs
  display flex
  gap 4px
  padding-left 6px

.tab
  flex 1 1 0
  min-height 44px
  padding 0 8px
  border 2px solid INK
  border-bottom 0
  border-radius 8px 8px 0 0
  background PAPER
  color INK
  font-family inherit
  font-size 0.95rem
  font-weight 650
  cursor pointer
  transition background-color 0.15s ease
  &:hover
    background WHITE
  &.tab--done
    color MUTED
  &.tab--active
    background WHITE
    color INK
    box-shadow inset 0 4px 0 LEMON

// ---------------------------------------------------------- feuille
.sheet-wrap
  position relative
  // Une seconde feuille dépasse derrière la première : le bloc de factures
  &::before
    content ''
    position absolute
    inset 10px -8px -10px 8px
    background PAPER
    border 2px solid INK
    border-radius 4px

.sheet
  position relative
  display flex
  flex-direction column
  // Hauteur identique pour les quatre étapes : la page ne saute pas quand on change d'onglet
  min-height 640px
  padding 20px 22px 24px
  background-color WHITE
  border 2px solid INK
  border-radius 4px
  overflow hidden
  &:focus-visible
    outline-offset 4px

.head
  display flex
  align-items flex-start
  justify-content space-between
  gap 12px
  padding-bottom 12px
  border-bottom 2px solid INK

.kind
  margin 0
  font-family 'Calder-LC', 'Trebuchet MS', system-ui, sans-serif
  font-size 1.55rem
  line-height 1.1

.number
  margin 4px 0 0
  color MUTED
  font-size 0.95rem

.sample
  margin 0
  padding 2px 8px
  border 1px dashed MUTED
  border-radius 3px
  color MUTED
  font-size 0.8rem
  white-space nowrap

.parties
  display flex
  flex-wrap wrap
  gap 0 6px
  margin 12px 0 6px
  font-size 0.92rem
  color MUTED
  strong
    color INK

.lines
  list-style none
  margin 0
  padding 0
  li
    display flex
    justify-content space-between
    align-items baseline
    gap 16px
    margin 0
    padding 8px 0
    border-bottom 1px solid RULE
  .label
    font-size 0.97rem
    small
      display block
      color MUTED
      font-size 0.82rem
  .amount
    white-space nowrap
    font-weight 600

.totals
  width 100%
  max-width 300px
  margin 14px 0 0 auto
  font-size 0.97rem
  > div
    display flex
    justify-content space-between
    gap 16px
    padding 3px 0
  dt, dd
    margin 0
  .total
    border-top 2px solid INK
    margin-top 6px
    padding-top 8px
    font-weight 700
  .deduction
    color MUTED
  .due
    margin-top 8px
    padding-top 8px
    border-top 1px solid INK
    font-size 1.12rem
    font-weight 700

// La note reste en bas de la feuille ; la marge de droite laisse la place au tampon
.footnote
  margin 0
  margin-top auto
  padding 18px 150px 0 0
  color MUTED
  font-size 0.88rem

// ---------------------------------------------------------- tampon
.stamp
  position absolute
  right 22px
  bottom 24px
  padding 6px 16px
  border 4px double currentColor
  border-radius 6px
  font-family 'Calder-LC', 'Trebuchet MS', system-ui, sans-serif
  font-size 1.5rem
  line-height 1.1
  letter-spacing 0.02em
  background rgba(255, 255, 255, 0.55)
  transform rotate(-9deg)
  transform-origin center
  animation stamp-in 0.38s cubic-bezier(0.2, 1.4, 0.4, 1) both
  pointer-events none

.stamp--ok
  color LEAF

.stamp--wait
  color BRICK

@keyframes stamp-in
  from
    opacity 0
    transform rotate(-9deg) scale(1.7)
  60%
    opacity 1
  to
    opacity 0.94
    transform rotate(-9deg) scale(1)

// ---------------------------------------------------------- légende
.caption
  margin 28px 0 0
  min-height 3.2em
  color INK_SOFT
  font-size 0.97rem
  a
    white-space nowrap

@media (max-width: TABLET_SIZE)
  .sheet
    padding 18px 16px 24px
    min-height 640px
  .stamp
    right 14px
    font-size 1.3rem
  .footnote
    padding-right 138px
  .kind
    font-size 1.35rem
  .sheet-wrap::before
    inset 8px -5px -8px 5px
</style>
